<template>
  <div class="mt-2">
    <b-breadcrumb class="breadcrumb-pipes">
      <b-breadcrumb-item href="/settings/admin-panel/">
        Admin Panel
      </b-breadcrumb-item>
      <b-breadcrumb-item href="/settings/user-role">
        Roles and Permissions
      </b-breadcrumb-item>
      <b-breadcrumb-item href="/settings/personal-settings">
        Personal Settings
      </b-breadcrumb-item>
    </b-breadcrumb>

    <b-card class="mb-0" title="Manage Global Variables">
      <b-overlay :show="loading" rounded="sm" no-fade>
        <div class="m-2">
          <b-row align-h="between">
            <b-col cols="12" md="3">
              <b-form-input
                v-model="filter"
                class="d-inline-block mr-1"
                placeholder="Search..."
              />
            </b-col>
            <b-col cols="12" md="3">
              <div class="d-flex align-items-center justify-content-end">
                <b-button variant="success" @click="openCreateModal">
                  <span class="text-nowrap">Add Global Variable</span>
                </b-button>
              </div>
            </b-col>
          </b-row>
        </div>

        <b-table
          striped
          hover
          ref="refGlobalVarTable"
          class="position-relative p-2"
          :items="allVariables"
          :filter="filter"
          filter-debounce="250"
          @filtered="onFiltered"
          :filter-included-fields="filterOn"
          selectable
          select-mode="single"
          responsive
          :small="true"
          head-variant="dark"
          :fields="tableColumns"
          show-empty
          empty-text="No matching records found"
          :current-page="currentPage"
          :per-page="perPage"
        >
          <template #cell(index)="data">
            <b><span class="font-weight-bold">
              <strong class="text-primary">#{{ data.index + 1 }}</strong>
            </span></b>
          </template>

          <template #cell(Name)="data">
            <strong>{{ data.item.Name || 'N/A' }}</strong>
          </template>

          <template #cell(Value)="data">
            <span>{{ data.item.Value || 'N/A' }}</span>
          </template>

          <template #cell(Type)="data">
            <b-badge :variant="typeVariant(data.item.Type)">
              {{ data.item.Type || 'N/A' }}
            </b-badge>
          </template>

          <template #cell(CreatedBy)="data">
            <span>{{ data.item.CreatedBy || 'N/A' }}</span>
          </template>

          <template #cell(actions)="data">
            <b-dropdown
              variant="link"
              no-caret
              :right="$store.state.appConfig.isRTL"
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <b-dropdown-item @click="openEditModal(data.item)">
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">Edit</span>
              </b-dropdown-item>
              <b-dropdown-item @click="confirmDelete(data.item)">
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50">Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>

        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted">
                Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries
              </span>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-overlay>

      <!-- Create Modal -->
      <b-modal
        v-model="createModal"
        title="Add Global Variable"
        @hidden="resetCreateForm"
        size="md"
        :ok-title="'Create'"
        cancel-title="Cancel"
        @ok="handleCreate"
      >
        <b-overlay :show="modalLoading" rounded="sm" no-fade>
          <b-form>
            <validation-observer ref="createFormValidation">
              <b-form-group label="Name" label-for="var-name">
                <validation-provider #default="{ errors }" name="Name" rules="required">
                  <b-form-input
                    id="var-name"
                    v-model="createForm.Name"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Variable name"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Value" label-for="var-value">
                <validation-provider #default="{ errors }" name="Value" rules="required">
                  <b-form-input
                    id="var-value"
                    v-model="createForm.Value"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Variable value"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Type" label-for="var-type">
                <validation-provider #default="{ errors }" name="Type" rules="required">
                  <v-select
                    id="var-type"
                    v-model="createForm.Type"
                    :options="typeOptions"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Select type"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </validation-observer>
          </b-form>
        </b-overlay>
      </b-modal>

      <!-- Edit Modal -->
      <b-modal
        v-model="editModal"
        title="Edit Global Variable"
        @hidden="resetEditForm"
        size="md"
        :ok-title="'Update'"
        cancel-title="Cancel"
        @ok="handleUpdate"
      >
        <b-overlay :show="modalLoading" rounded="sm" no-fade>
          <b-form>
            <validation-observer ref="editFormValidation">
              <b-form-group label="Name" label-for="edit-var-name">
                <validation-provider #default="{ errors }" name="Name" rules="required">
                  <b-form-input
                    id="edit-var-name"
                    v-model="editForm.Name"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Variable name"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Value" label-for="edit-var-value">
                <validation-provider #default="{ errors }" name="Value" rules="required">
                  <b-form-input
                    id="edit-var-value"
                    v-model="editForm.Value"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Variable value"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Type" label-for="edit-var-type">
                <validation-provider #default="{ errors }" name="Type" rules="required">
                  <v-select
                    id="edit-var-type"
                    v-model="editForm.Type"
                    :options="typeOptions"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Select type"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </validation-observer>
          </b-form>
        </b-overlay>
      </b-modal>
    </b-card>
  </div>
</template>

<script>
import {
  BOverlay,
  BCard,
  BRow,
  BCol,
  BFormInput,
  BFormGroup,
  BButton,
  BTable,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BModal,
  BForm,
  BBreadcrumb,
  BBreadcrumbItem,
  VBModal,
} from 'bootstrap-vue';
import store from '@/store';
import { onUnmounted } from '@vue/composition-api';
import settingsStoreModule from '../../settingsStoreModule';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { required } from '@validations';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import Ripple from 'vue-ripple-directive';
import vSelect from 'vue-select';

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BFormGroup,
    BButton,
    BTable,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BModal,
    BForm,
    BBreadcrumb,
    BBreadcrumbItem,
    ValidationProvider,
    ValidationObserver,
    ToastificationContent,
    vSelect,
  },

  directives: {
    Ripple,
    'b-modal': VBModal,
  },

  setup() {
    const SETTINGS_APP_STORE_MODULE_NAME = 'settings';
    if (!store.hasModule(SETTINGS_APP_STORE_MODULE_NAME))
      store.registerModule(SETTINGS_APP_STORE_MODULE_NAME, settingsStoreModule);

    onUnmounted(() => {
      if (store.hasModule(SETTINGS_APP_STORE_MODULE_NAME))
        store.unregisterModule(SETTINGS_APP_STORE_MODULE_NAME);
    });
  },

  data() {
    return {
      allVariables: [],
      loading: false,
      modalLoading: false,
      createModal: false,
      editModal: false,
      createForm: { Name: null, Value: null, Type: null },
      editForm: { id: null, Name: null, Value: null, Type: null },
      typeOptions: ['String', 'Number', 'Boolean', 'JSON'],
      filter: null,
      filterOn: ['Name', 'Value', 'Type'],
      tableColumns: [
        { key: 'index', label: '' },
        { key: 'Name', label: 'Name', sortable: true },
        { key: 'Value', label: 'Value', sortable: true },
        { key: 'Type', label: 'Type', sortable: true },
        { key: 'CreatedBy', label: 'Created By', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      perPage: 10,
      currentPage: 1,
      totalRows: 0,
    };
  },

  computed: {
    dataMeta() {
      return {
        from: this.totalRows !== 0 ? this.perPage * (this.currentPage - 1) + 1 : 0,
        to:
          this.perPage * (this.currentPage - 1) + Number(this.perPage) < this.totalRows
            ? this.perPage * (this.currentPage - 1) + Number(this.perPage)
            : this.totalRows,
        of: this.totalRows,
      };
    },
  },

  mounted() {
    this.getGlobalVariables();
  },

  methods: {
    getGlobalVariables() {
      this.loading = true;
      store
        .dispatch('settings/getGlobalVariables')
        .then((res) => {
          this.allVariables = res.data;
          this.totalRows = res.data.length;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.showErrorToast('Error fetching global variables.');
        });
    },

    openCreateModal() {
      this.createModal = true;
    },

    openEditModal(item) {
      this.editForm = { id: item.id, Name: item.Name, Value: item.Value, Type: item.Type };
      this.editModal = true;
    },

    handleCreate(bvModalEvent) {
      bvModalEvent.preventDefault();
      this.$refs.createFormValidation.validate().then((success) => {
        if (success) {
          this.modalLoading = true;
          store
            .dispatch('settings/createGlobalVariable', this.createForm)
            .then((res) => {
              this.modalLoading = false;
              if (res.data.info === 'exist_name') {
                this.showWarningToast('A global variable with this name already exists.');
              } else if (res.data.status) {
                this.createModal = false;
                this.showSuccessToast('Global variable created successfully.');
                this.getGlobalVariables();
              } else {
                this.showErrorToast('Something went wrong.');
              }
            })
            .catch(() => {
              this.modalLoading = false;
              this.showErrorToast('Something went wrong.');
            });
        }
      });
    },

    handleUpdate(bvModalEvent) {
      bvModalEvent.preventDefault();
      this.$refs.editFormValidation.validate().then((success) => {
        if (success) {
          this.modalLoading = true;
          store
            .dispatch('settings/updateGlobalVariable', this.editForm)
            .then((res) => {
              this.modalLoading = false;
              if (res.data.info === 'exist_name') {
                this.showWarningToast('A global variable with this name already exists.');
              } else if (res.data.status) {
                this.editModal = false;
                this.showSuccessToast('Global variable updated successfully.');
                this.getGlobalVariables();
              } else {
                this.showErrorToast('Something went wrong.');
              }
            })
            .catch(() => {
              this.modalLoading = false;
              this.showErrorToast('Something went wrong.');
            });
        }
      });
    },

    confirmDelete(item) {
      this.$bvModal
        .msgBoxConfirm(`Are you sure you want to delete "${item.Name}"?`, {
          title: 'Confirm Delete',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          centered: true,
        })
        .then((confirmed) => {
          if (confirmed) {
            this.deleteVariable(item);
          }
        });
    },

    deleteVariable(item) {
      this.loading = true;
      store
        .dispatch('settings/deleteGlobalVariable', { id: item.id })
        .then((res) => {
          if (res.data.status) {
            this.showSuccessToast('Global variable deleted successfully.');
            this.getGlobalVariables();
          } else {
            this.loading = false;
            this.showErrorToast('Something went wrong.');
          }
        })
        .catch(() => {
          this.loading = false;
          this.showErrorToast('Something went wrong.');
        });
    },

    resetCreateForm() {
      this.createForm = { Name: null, Value: null, Type: null };
      this.modalLoading = false;
      if (this.$refs.createFormValidation) this.$refs.createFormValidation.reset();
    },

    resetEditForm() {
      this.editForm = { id: null, Name: null, Value: null, Type: null };
      this.modalLoading = false;
      if (this.$refs.editFormValidation) this.$refs.editFormValidation.reset();
    },

    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    typeVariant(type) {
      const map = {
        String: 'light-primary',
        Number: 'light-success',
        Boolean: 'light-warning',
        JSON: 'light-info',
      };
      return map[type] || 'light-secondary';
    },

    showSuccessToast(text) {
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Success', text, icon: 'ThumbsUpIcon', variant: 'success' },
      });
    },

    showWarningToast(text) {
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Warning', text, icon: 'AlertTriangleIcon', variant: 'warning' },
      });
    },

    showErrorToast(text) {
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Error', text, icon: 'AlertTriangleIcon', variant: 'danger' },
      });
    },
  },

  watch: {
    perPage: {
      handler(val) {
        localStorage.setItem('globalVarShow', val);
      },
    },
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
